/**
 * Admin Authentication Service
 * Clean Architecture: Infrastructure Layer for admin API communication
 * 
 * Handles authentication with the super-admin portal backend.
 * 
 * Author: CleverAct AI Development Team
 * Created: 2025-07-19
 */

import { API_BASE_URL } from '../utils/constants'

// Base URL for admin API
const ADMIN_API_BASE = `${API_BASE_URL}/admin/auth`;

// Types for admin authentication
export interface AdminUser {
  id: string;
  email: string;
  full_name: string;
  role: string;
  permissions: Record<string, string[]>;
  is_active: boolean;
  is_verified: boolean;
  last_login: string | null;
  created_at: string;
  failed_login_attempts: number;
}

export interface AdminLoginResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
  admin: AdminUser;
}

export interface AdminLoginRequest {
  email: string;
  password: string;
}

export interface CreateAdminRequest {
  email: string;
  full_name: string;
  role: string;
  permissions?: Record<string, string[]>;
  password?: string;
}

export interface UpdateAdminRequest {
  full_name?: string;
  role?: string;
  permissions?: Record<string, string[]>;
  is_active?: boolean;
}

export interface PasswordChangeRequest {
  current_password: string;
  new_password: string;
}

export interface PermissionCheckRequest {
  resource: string;
  action: string;
}

// Admin authentication service
class AdminAuthService {
  private baseUrl = ADMIN_API_BASE;

  /**
   * Login to admin portal
   */
  async login(email: string, password: string): Promise<AdminLoginResponse> {
    const response = await fetch(`${this.baseUrl}/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, password }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || 'Login failed');
    }

    const data = await response.json();
    
    // Store token for subsequent requests
    this.setAuthToken(data.access_token);
    
    return data;
  }

  /**
   * Logout from admin portal
   */
  async logout(): Promise<void> {
    const token = this.getAuthToken();
    
    if (token) {
      try {
        await fetch(`${this.baseUrl}/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
      } catch (error) {
        console.warn('Logout request failed:', error);
      }
    }

    // Clear stored token
    this.clearAuthToken();
  }

  /**
   * Get current admin user info
   */
  async getCurrentAdmin(): Promise<AdminUser> {
    const response = await this.authenticatedRequest(`${this.baseUrl}/me`);
    return response;
  }

  /**
   * Change admin password
   */
  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    const request: PasswordChangeRequest = {
      current_password: currentPassword,
      new_password: newPassword,
    };

    await this.authenticatedRequest(`${this.baseUrl}/password`, {
      method: 'PUT',
      body: JSON.stringify(request),
    });
  }

  /**
   * Check if admin has specific permission
   */
  async checkPermission(resource: string, action: string): Promise<boolean> {
    const request: PermissionCheckRequest = { resource, action };
    
    const response = await this.authenticatedRequest(`${this.baseUrl}/permissions/check`, {
      method: 'POST',
      body: JSON.stringify(request),
    });

    return response.has_permission;
  }

  /**
   * Get all permissions for current admin
   */
  async getPermissions(): Promise<Record<string, string[]>> {
    const response = await this.authenticatedRequest(`${this.baseUrl}/permissions`);
    return response.permissions;
  }

  /**
   * Create new admin user
   */
  async createAdmin(adminData: CreateAdminRequest): Promise<AdminUser> {
    const response = await this.authenticatedRequest(`${this.baseUrl}/admins`, {
      method: 'POST',
      body: JSON.stringify(adminData),
    });

    return response;
  }

  /**
   * Get all admin users
   */
  async getAdmins(options?: {
    include_inactive?: boolean;
    role_filter?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ admins: AdminUser[]; total: number; limit: number; offset: number }> {
    const params = new URLSearchParams();
    
    if (options?.include_inactive) params.append('include_inactive', 'true');
    if (options?.role_filter) params.append('role_filter', options.role_filter);
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.offset) params.append('offset', options.offset.toString());

    const url = `${this.baseUrl}/admins${params.toString() ? `?${params.toString()}` : ''}`;
    return await this.authenticatedRequest(url);
  }

  /**
   * Update admin user
   */
  async updateAdmin(adminId: string, updateData: UpdateAdminRequest): Promise<AdminUser> {
    const response = await this.authenticatedRequest(`${this.baseUrl}/admins/${adminId}`, {
      method: 'PUT',
      body: JSON.stringify(updateData),
    });

    return response;
  }

  /**
   * Delete admin user
   */
  async deleteAdmin(adminId: string): Promise<void> {
    await this.authenticatedRequest(`${this.baseUrl}/admins/${adminId}`, {
      method: 'DELETE',
    });
  }

  /**
   * Make authenticated request to admin API
   */
  private async authenticatedRequest(url: string, options: RequestInit = {}): Promise<any> {
    const token = this.getAuthToken();
    
    if (!token) {
      throw new Error('Admin authentication required');
    }

    const response = await fetch(url, {
      ...options,
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });

    if (response.status === 401) {
      // Token expired or invalid
      this.clearAuthToken();
      throw new Error('Admin session expired. Please login again.');
    }

    if (response.status === 403) {
      throw new Error('Insufficient permissions for this action');
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `Request failed with status ${response.status}`);
    }

    return await response.json();
  }

  /**
   * Store authentication token
   */
  private setAuthToken(token: string): void {
    localStorage.setItem('adminToken', token);
  }

  /**
   * Get stored authentication token
   */
  private getAuthToken(): string | null {
    return localStorage.getItem('adminToken');
  }

  /**
   * Clear stored authentication token
   */
  private clearAuthToken(): void {
    localStorage.removeItem('adminToken');
  }

  /**
   * Check if admin is authenticated
   */
  isAuthenticated(): boolean {
    const token = this.getAuthToken();
    if (!token) return false;

    try {
      // Decode JWT payload to check expiration
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Date.now() / 1000;
      
      return payload.exp > currentTime;
    } catch (error) {
      console.warn('Invalid admin token format:', error);
      this.clearAuthToken();
      return false;
    }
  }

  /**
   * Get admin info from stored token
   */
  getAdminFromToken(): Partial<AdminUser> | null {
    const token = this.getAuthToken();
    if (!token) return null;

    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return {
        id: payload.sub,
        email: payload.email,
        role: payload.role,
      };
    } catch (error) {
      console.warn('Invalid admin token format:', error);
      return null;
    }
  }
}

// Export singleton instance
export const adminAuthService = new AdminAuthService();
export default adminAuthService;
